/**
 * Visual-viewport tracking for reliable mobile layout (CLE-114, CLE-116, CLE-120).
 *
 * iOS Safari's `100dvh` does NOT shrink when the on-screen keyboard opens: the
 * dynamic viewport unit only accounts for the collapsing browser toolbar, not
 * the keyboard. Worse, when an input near the bottom of the page is focused,
 * iOS pans the *layout viewport* up to bring it into view — flinging the whole
 * page (header + chat) off-screen and hiding the composer (see CLE-91, CLE-106,
 * CLE-114, CLE-116).
 *
 * The `visualViewport` API reports the *actually visible* area, keyboard
 * included. We mirror its geometry into CSS custom properties on the root
 * element so layout can size the chat shell to exactly the visible region —
 * which collapses with the keyboard. The chat shell (`.chat-shell`) is a normal
 * flex column constrained to `--app-height` (NOT `position: fixed` — CLE-120):
 *
 *   --app-height      total visible viewport height in px (keyboard-aware)
 *   --vh              1% of that height, for `calc(var(--vh) * 100)` math
 *   --keyboard-inset  px the keyboard (or toolbar) currently overlaps the layout
 *   --viewport-offset-top  px the visual viewport is panned down from the layout
 *                          viewport top (non-zero only mid focus-scroll)
 *
 * Layout reads these with graceful fallbacks (`100dvh` -> `100vh`) so browsers
 * without `visualViewport` keep the previous behaviour.
 */

/**
 * Whether the layout viewport should be force-pinned to the top on every visual
 * viewport change. This is the iOS keyboard workaround and MUST only be active
 * on the AI Assistant chat page (CLE-119): pinning the document to (0, 0) makes
 * the whole page unscrollable, which is correct for chat (only the message list
 * scrolls) but breaks every normal page (Daily Summary, Dashboard, etc.).
 *
 * Pages that need the lock call `setViewportPinned(true)` on mount and
 * `setViewportPinned(false)` on unmount. Default OFF so ordinary pages scroll.
 */
let pinViewport = false;

/**
 * Enable/disable layout-viewport pinning (the chat keyboard workaround).
 *
 * Only the chat page should turn this on. While disabled, the CSS custom
 * properties below are still mirrored (harmless and useful everywhere) but the
 * document is left free to scroll normally.
 */
export function setViewportPinned(pinned: boolean): void {
  pinViewport = pinned;
}

/**
 * Is the app running installed (PWA), rather than inside browser chrome?
 *
 * The distinction matters below: installed, there are no collapsible toolbars,
 * so the layout viewport is exactly the available area. `matchMedia` is cached
 * because `update()` runs on every viewport scroll event.
 */
let standaloneQuery: MediaQueryList | null | undefined;
function isStandalone(): boolean {
  if (standaloneQuery === undefined) {
    standaloneQuery = window.matchMedia?.('(display-mode: standalone)') ?? null;
  }
  // `navigator.standalone` is the pre-`display-mode` iOS flag; still the only
  // reliable signal on some iOS versions when launched from the home screen.
  return (
    standaloneQuery?.matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Does the focused element summon the on-screen keyboard? */
function hasKeyboardFocus(): boolean {
  const el = document.activeElement;
  if (!el || !(el instanceof HTMLElement)) return false;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    el.isContentEditable
  );
}

/** Recompute the viewport custom properties from the current visual viewport. */
function update(): void {
  const root = document.documentElement;
  const vv = window.visualViewport;

  // While the page is zoomed in, `visualViewport.height` describes the magnified
  // window rather than the page: sizing the shell to it would collapse the whole
  // layout into a sliver and pan it out of reach, with no way back except
  // pinching out again. Ignore the visual viewport until the zoom is gone and
  // fall back to the layout viewport, which stays honest at any scale.
  const usable = vv && Math.abs(vv.scale - 1) <= 0.01 ? vv : null;
  const zoomed = Boolean(vv) && usable === null;

  // Installed as a PWA there is no browser chrome, so the layout viewport IS the
  // whole available area and the keyboard is the only thing that can shrink the
  // visual one. iOS regularly leaves a stale, too-short `visualViewport` behind
  // after the keyboard animates away — its last `resize` fires mid-flight — which
  // stranded a permanent band of dead space under the chat composer. When nothing
  // is focused the keyboard cannot be up, so the short reading is wrong by
  // definition: trust the layout viewport instead.
  //
  // Deliberately installed-only. In Safari proper `innerHeight` also spans the
  // area behind the collapsible toolbars, so preferring it there would slide the
  // composer back underneath them (CLE-91).
  const staleKeyboardInset = isStandalone() && !hasKeyboardFocus();

  // Visible height: prefer the visual viewport (keyboard-aware) and fall back to
  // the layout viewport when the API is unavailable or the page is zoomed.
  const trusted = staleKeyboardInset ? null : usable;
  const height = trusted?.height ?? window.innerHeight;
  const offsetTop = trusted?.offsetTop ?? 0;

  // How much the bottom of the layout viewport is covered by the keyboard. When
  // the keyboard is closed this is ~0. We clamp to >= 0 to avoid negative insets
  // from sub-pixel rounding or toolbar animations.
  const layoutHeight = window.innerHeight;
  const inset = Math.max(0, layoutHeight - height - offsetTop);

  root.style.setProperty('--app-height', `${Math.round(height)}px`);
  root.style.setProperty('--vh', `${height / 100}px`);
  root.style.setProperty('--keyboard-inset', `${Math.round(inset)}px`);
  root.style.setProperty('--viewport-offset-top', `${Math.round(offsetTop)}px`);
  // A coarse "is the keyboard up" flag for CSS (the chat composer drops its
  // home-indicator padding while it is). The threshold is well above any
  // toolbar animation and well below the shortest keyboard.
  root.dataset.keyboard = inset > 80 ? 'open' : 'closed';

  // Backstop (CLE-116 / CLE-119 / CLE-120): iOS may still scroll the *layout
  // viewport* to reveal a focused input, panning the whole page up so the
  // composer disappears off the top. On chat the shell already locks html/body
  // overflow and owns the keyboard-aware height, so the document should never
  // need to scroll — gently undo any stray iOS auto-scroll by snapping back to
  // the top whenever the visual viewport changes.
  //
  // This pin is opt-in (chat only). On every other page it would trap scrolling
  // (the visual viewport `scroll`/`resize` events fire while the user scrolls or
  // the toolbar collapses, snapping the page straight back to the top), so it is
  // gated behind `pinViewport`.
  // Suspended while zoomed in: panning around a magnified page is the only way
  // to read it, and snapping back to the origin on every pan would fight the
  // user instead of helping.
  if (pinViewport && !zoomed && (window.scrollY !== 0 || window.scrollX !== 0)) {
    window.scrollTo(0, 0);
  }
}

let started = false;

/**
 * Begin tracking the visual viewport and keep the CSS custom properties in sync.
 *
 * Safe to call once at app startup (see main.tsx). Idempotent and a no-op in
 * non-browser / unsupported environments.
 */
export function initViewportTracking(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  update();

  const vv = window.visualViewport;
  if (vv) {
    // `resize` fires when the keyboard opens/closes or the toolbar collapses;
    // `scroll` fires when the viewport is panned (e.g. focus scrolls an input
    // into view), which also shifts offsetTop and thus the keyboard inset.
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
  }
  // Orientation changes and layout-viewport resizes still need a refresh even
  // when visualViewport is present (e.g. rotating the device).
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);

  // Installed on iOS, `innerHeight` at first paint is sometimes the previous
  // session's or orientation's value and no `resize` follows; the same after
  // coming back from the app switcher. Re-measure on those transitions and
  // once the launch has settled, so a stale height cannot strand a band of
  // dead space under the chat composer.
  window.addEventListener('pageshow', update);
  document.addEventListener('visibilitychange', update);
  setTimeout(update, 300);
  setTimeout(update, 1000);

  // Focus drives the `staleKeyboardInset` branch above, and losing focus is
  // precisely when the visual viewport goes stale, so recompute on both.
  window.addEventListener('focusin', update);
  window.addEventListener('focusout', () => {
    // `focusout` fires *before* activeElement moves, so read it a task later.
    setTimeout(update, 0);
    // The keyboard keeps animating away for a few hundred ms; iOS may emit one
    // last mid-flight `resize` after the blur. Re-settle once it has landed.
    setTimeout(update, 400);
  });
}
